import { num, seed, daysUntil } from "./utils";

export const DEALERS=[{id:"driveway",name:"Driveway",factor:.936},{id:"carvana",name:"Carvana",factor:.921},{id:"vroom",name:"Vroom",factor:.913},{id:"carmax",name:"CarMax",factor:.897},{id:"kbb",name:"KBB Instant",factor:.887},{id:"autonation",name:"AutoNation",factor:.876},{id:"echo",name:"EchoPark",factor:.906},{id:"carlotz",name:"CarLotz",factor:.866}];
export const INSURERS=[{name:"Root",factor:.68},{name:"Lemonade",factor:.71},{name:"GEICO",factor:.74},{name:"Progressive",factor:.78},{name:"State Farm",factor:.82}];

export function getMileageAdjustedValue(v){const base=num(v.value);if(!base)return base;if(v.year<1980)return base;const age=Math.max(1,new Date().getFullYear()-v.year),expectedMi=age*12000,deltaMi=num(v.mileage)-expectedMi,rawAdj=-deltaMi*0.08,cappedAdj=Math.max(-base*0.15,Math.min(base*0.15,rawAdj));return Math.round((base+cappedAdj)/100)*100;}
/* Client-side mirror of functions/_lib/enrichFinancials.js's simulateValue —
   same make/model/year/mileage formula, kept in sync deliberately so a
   vehicle never shows two different "estimated" numbers depending on
   whether the figure came from the server enrich pipeline or this local
   fallback. Used by resolveValue below for any vehicle with no manually-
   entered value AND no server-resolved financials.value — previously
   that combination (e.g. a freshly walkthrough-added vehicle whose one
   real pull was refused) fell through to a bare 0/null with no estimate
   at all. Returns null for pre-1980 classics, same as the server: no
   formulaic baseline for those, needs real research. */
export function simulateValue(make,model,year,mileage){
  if(year&&year<1980)return null;
  const m=String(model||"").toLowerCase();
  const isTruck=/tundra|f-150|f150|silverado|sierra|ram|tacoma|ranger|colorado|frontier/.test(m);
  const isSuv=/tahoe|suburban|expedition|explorer|highlander|4runner|pilot|traverse|yukon/.test(m);
  const isLux=/bmw|mercedes|audi|lexus|porsche|tesla|genesis|land rover/.test(String(make||"").toLowerCase());
  const s=seed(String(make||"")+model+String(year||""));
  const age=Math.max(1,new Date().getFullYear()-(year||new Date().getFullYear()));
  let base=isLux?42000:isTruck?38000:isSuv?34000:24000;
  base=base*Math.pow(0.88,age-1);
  const jitter=1+(s*0.16-0.08);
  const mi=mileage?Number(mileage):age*12000;
  const expectedMi=age*12000;
  const miAdj=1-Math.max(-0.15,Math.min(0.15,(mi-expectedMi)*0.000004));
  return Math.max(1500,Math.round((base*jitter*miAdj)/100)*100);
}
export function getBestOffer(v,adjVal){const base=adjVal??getMileageAdjustedValue(v);if(!base)return null;let best=null;DEALERS.forEach((d,i)=>{const amt=Math.round((base*d.factor+base*(seed(v.id,i)*.04-.02))/100)*100;if(!best||amt>best.amount)best={dealer:d,amount:amt};});return best;}
export function getRefiRate(v){return+Math.max(3.9,5.4+(new Date().getFullYear()-v.year)*.1+seed(v.id,99)*.6).toFixed(2);}
export function getBestIns(v){const base=num(v.insurance);if(!base)return null;let best=null;INSURERS.forEach((ins,i)=>{const q=Math.round(base*(ins.factor+seed(v.id,i+50)*.06-.03));if(!best||q<best.quote)best={name:ins.name,quote:q};});return best;}

export async function fetchMarketValueLive(vin,mileage,type){
  if(!vin||vin.replace(/\s/g,"").length<11)return null;
  try{
    const q=new URLSearchParams({vin:vin.replace(/\s/g,"").toUpperCase()});
    if(mileage)q.set("mileage",String(mileage));
    if(type)q.set("type",type);
    const res=await fetch(`/api/vinaudit?${q.toString()}`);
    if(!res.ok)return null;
    const d=await res.json();
    if(!d.success||!d.prices?.average)return null;
    return{amount:Math.round(d.prices.average/100)*100,low:d.prices.below,high:d.prices.above,source:"vinaudit"};
  }catch{return null;}
}
/* liveValues is a {[vehicleId]: {amount,low,high,source}} cache populated by the root component when in live mode.
   Falls back to the mileage-adjusted formula for classics, no-VIN vehicles, or any vehicle VinAudit has no comps for.
   Still used by clydeAssistant.js's chat context — not part of the resolve* family below. */
export function resolveAdjValue(v,liveValues){
  const live=liveValues&&liveValues[v.id];
  return live?live.amount:getMileageAdjustedValue(v);
}

/* ─── Shared resolvers: the single place VehicleRow.jsx and HomeFleetApp.jsx
   ask "what's this vehicle's authoritative value/offer/rate/quote". Each
   prefers the vehicle's server-resolved vehicle_financials figure (real/
   researched/estimated, written by functions/api/vehicles/[id]/enrich.js)
   and only falls back to the old client-side formula for a vehicle that's
   never been enriched — a fresh seed row, or one added before this system
   existed. The formula fallback is itself labeled "estimated"/"formula",
   same vocabulary as the enrich pipeline's own bottom tier, so a caller
   never needs to know which path a figure came from to badge it.
   None of these name a specific dealer or insurer — a formula guess or a
   researched web figure was never a real quote from a named company. */
export function resolveValue(v){
  const fin=v.financials?.value;
  if(fin&&fin.amount!=null)return{amount:fin.amount,source:fin.source,origin:fin.origin,low:fin.low,high:fin.high};
  const manual=getMileageAdjustedValue(v);
  const amount=manual||simulateValue(v.make,v.model,v.year,v.mileage);
  return{amount,source:"estimated",origin:"formula",low:null,high:null};
}
export function resolveBestOffer(v,valueAmount){
  const fin=v.financials?.best_offer;
  if(fin&&fin.amount!=null)return{amount:fin.amount,source:fin.source,origin:fin.origin,low:fin.low,high:fin.high};
  if(valueAmount==null)return null;
  const o=getBestOffer(v,valueAmount);
  return o?{amount:o.amount,source:"estimated",origin:"formula",low:null,high:null}:null;
}
export function resolveInsuranceQuote(v){
  const fin=v.financials?.insurance_quote;
  if(fin&&fin.amount!=null)return{amount:fin.amount,source:fin.source,origin:fin.origin,low:fin.low,high:fin.high};
  const q=getBestIns(v);
  return q?{amount:q.quote,source:"estimated",origin:"formula",low:null,high:null}:null;
}
export function resolveRefiRate(v){
  const fin=v.financials?.refi_rate;
  if(fin&&fin.amount!=null)return{amount:fin.amount,source:fin.source,origin:fin.origin,low:fin.low,high:fin.high};
  return{amount:getRefiRate(v),source:"estimated",origin:"formula",low:null,high:null};
}
